import { AuthBagService } from '@/auth/auth-bag.service';
import { AuthService } from '@/auth/auth.service';
import { Result } from '@/reservation/reservation.model';
import { HttpClient, HttpEventType, HttpHeaders, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, catchError, of, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { LoaderService } from './loader/loader.service';

@Injectable({
	providedIn: 'root',
})
export class Data2Service {
	constructor(
		private readonly http: HttpClient,
		private readonly auth: AuthService,
		private readonly authBag: AuthBagService,
		private readonly loader: LoaderService,
	) {}

	// eslint-disable-next-line  @typescript-eslint/no-explicit-any
	get<T = any>(url: string, callback: (result: T | false, error: any) => void): void {
		this.http
			.get(environment.erpUrl + url, this.httpHeaders())
			.pipe(
				catchError((error) => {
					if (error.status === 401) {
						this.userUnauthed();
						return of(); // throwError(() => new Error(error));
					}
					callback(false, error);
					return of();
				}),
			)
			.subscribe((result: T) => {
				callback(result, false);
			});
	}

	// eslint-disable-next-line  @typescript-eslint/no-explicit-any
	put<T = any>(url: string, data: object, callback: (result: T | false, error: any) => void): void {
		this.http
			.put(environment.erpUrl + url, data, this.httpHeaders())
			.pipe(
				catchError((error) => {
					if (error.status === 401) {
						this.userUnauthed();
						return throwError(() => new Error(error));
					}
					callback(false, error);
					return of();
				}),
			)
			.subscribe((result: T) => {
				callback(result, false);
			});
	}

	// eslint-disable-next-line  @typescript-eslint/no-explicit-any
	post<T = any>(url: string, data: object, callback: (result: T | false, error: any) => void): void {
		this.http
			.post(environment.erpUrl + url, data, this.httpHeaders())
			.pipe(
				catchError((error) => {
					if (error.status === 401) {
						this.userUnauthed();
						return throwError(() => new Error(error));
					}
					callback(false, error);
					return of();
				}),
			)
			.subscribe((result: T) => {
				callback(result, false);
			});
	}

	// eslint-disable-next-line  @typescript-eslint/no-explicit-any
	del<T = any>(url: string, callback: (result: T | false, error: any) => void): void {
		this.http
			.delete(environment.erpUrl + url, this.httpHeaders())
			.pipe(
				catchError((error) => {
					if (error.status === 401) {
						this.userUnauthed();
						return throwError(() => new Error(error));
					}
					callback(false, error);
					return of();
				}),
			)
			.subscribe((result: T) => {
				callback(result, false);
			});
	}

	// eslint-disable-next-line  @typescript-eslint/no-explicit-any
	command<T = any>(url: string, data: object, callback: (result: T | false, error: any) => void): void {
		this.http
			.patch(environment.erpUrl + url, data, this.httpHeaders())
			.pipe(
				catchError((error) => {
					if (error.status === 401) {
						this.userUnauthed();
						return throwError(() => new Error(error));
					}
					callback(false, error);
					return of();
				}),
			)
			.subscribe((result: T) => {
				callback(result, false);
			});
	}

	upload(url: string, files: Set<File>): { [key: string]: { progress: Observable<number> } } {
		// this will be the our resulting map
		const status: { [key: string]: { progress: Observable<number> } } = {};

		files.forEach((file) => {
			// create a new multipart-form for every file
			const formData: FormData = new FormData();
			formData.append('file', file, file.name);

			// create a http-post request and pass the form
			// tell it to report the upload progress
			const req = new HttpRequest('POST', environment.erpUrl + url, formData, {
				reportProgress: true,
				headers: new HttpHeaders({
					Authorization: this.auth.token2Get(),
				}),
			});

			// create a new progress-subject for every file
			const progress = new Subject<number>();

			// send the http-request and subscribe for progress-updates

			this.http.request(req).subscribe((event) => {
				if (event.type === HttpEventType.UploadProgress) {
					// calculate the progress percentage

					const percentDone = Math.round((100 * event.loaded) / event.total);
					// pass the percentage into the progress-stream
					progress.next(percentDone);
				} else if (event instanceof HttpResponse) {
					// Close the progress-stream if we get an answer form the API
					// The upload is complete
					progress.complete();
				}
			});

			// Save every progress-observable in a map of all observables
			status[file.name] = {
				progress: progress.asObservable(),
			};
		});

		// return the map of progress.observables
		return status;
	}

	uploadFile(url: string, file: File): Observable<Result<unknown>> {
		const formData: FormData = new FormData();
		formData.append('name', file.name);
		formData.append('file', file);
		return this.http
			.post<Result<unknown>>(environment.erpUrl + url, formData, {
				reportProgress: true,
				headers: new HttpHeaders({
					Authorization: this.auth.token2Get(),
				}),
			})
			.pipe(
				catchError((error) => {
					this.loader.hide();
					return throwError(() => error);
				}),
			);
	}

	// eslint-disable-next-line  @typescript-eslint/no-explicit-any
	typeParametersConvert<T = any>(data: T[], parameterId: number): T[] {
		const newParms = [];
		// eslint-disable-next-line  @typescript-eslint/no-explicit-any
		const parameters = data.filter((obj: any) => obj.type_parameters_id == parameterId);
		// eslint-disable-next-line  @typescript-eslint/no-explicit-any
		parameters.forEach((obj: any) => {
			newParms.push({
				id: obj.code,
				name: obj.polish,
			});
		});
		return this.sortAlphabetically(newParms);
	}

	// eslint-disable-next-line  @typescript-eslint/no-explicit-any
	sortAlphabetically<T = any>(data: T[], byKey = 'name'): T[] {
		return data.sort((a, b) => a[byKey].localeCompare(b[byKey]));
	}

	// eslint-disable-next-line  @typescript-eslint/no-explicit-any
	getChecked<T = any>(filters: T, parameters = null): string {
		let checked = '';
		for (const key in filters) {
			if (typeof filters[key] === 'boolean') {
				if (filters[key]) {
					checked += '&' + key + '=1';
				} else {
					checked += '&' + key + '=0';
				}
				continue;
			}
			if (typeof filters[key] === 'number') {
				checked += '&' + key + '=' + filters[key];
				continue;
			}
			if (!filters[key]) {
				continue;
			}
			if (Array.isArray(filters[key])) {
				// eslint-disable-next-line  @typescript-eslint/no-explicit-any
				if (!parameters || !(filters[key] as any[]).length) {
					continue;
				}
				const objects = [];
				// eslint-disable-next-line  @typescript-eslint/no-explicit-any
				(filters[key] as any[]).forEach((obj) => {
					if (!obj) {
						return;
					}
					objects.push(obj);
				});
				checked += '&' + key + '=' + objects;
				continue;
			}
			checked += '&' + key + '=' + filters[key];
		}
		return checked.replace(/^&/, '');
	}

	getPromise<T = { data: unknown }>(url: string): Promise<T> {
		return new Promise((resolve, reject) => {
			this.get(url, (result, error) => {
				if (error) {
					reject(error);
				} else {
					resolve(result);
				}
			});
		});
	}

	private httpHeaders(): { headers: HttpHeaders } {
		return { headers: this.httpHeadersGet() };
	}

	private httpHeadersGet(): HttpHeaders {
		if (typeof this.auth.token2Get() === 'undefined') {
			return new HttpHeaders({ 'Content-Type': 'application/json' });
		}

		return new HttpHeaders({
			'Content-Type': 'application/json',
			Authorization: this.auth.token2Get(),
		});
	}

	private userUnauthed(): void {
		this.authBag.unauth();
	}
}
